<script setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useFunctionGetter, useMapGetter } from 'dashboard/composables/store';
import wootConstants from 'dashboard/constants/globals';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import Banner from 'dashboard/components/ui/Banner.vue';

const props = defineProps({
  contactId: {
    type: [Number, String],
    default: null,
  },
  currentConversationId: {
    type: [Number, String],
    default: null,
  },
});

const emit = defineEmits(['showPreviousConversations']);

const { t } = useI18n();
const router = useRouter();
const accountId = useMapGetter('getCurrentAccountId');
const activeInbox = useMapGetter('getSelectedInbox');

const conversations = useFunctionGetter(
  'contactConversations/getContactConversation',
  computed(() => props.contactId)
);

// `id` on these records is the conversation display_id, the same value the
// route and `getSelectedChat` use.
const openConversations = computed(() =>
  conversations.value.filter(
    conversation =>
      conversation.status === wootConstants.STATUS_TYPE.OPEN &&
      Number(conversation.id) !== Number(props.currentConversationId)
  )
);

const singleConversation = computed(() =>
  openConversations.value.length === 1 ? openConversations.value[0] : null
);

const bannerMessage = computed(() => {
  if (singleConversation.value) {
    return t('CONVERSATION.OPEN_CONVERSATIONS_BANNER.SINGLE', {
      id: singleConversation.value.id,
    });
  }
  return t('CONVERSATION.OPEN_CONVERSATIONS_BANNER.MULTIPLE', {
    count: openConversations.value.length,
  });
});

const actionLabel = computed(() =>
  singleConversation.value
    ? t('CONVERSATION.OPEN_CONVERSATIONS_BANNER.OPEN')
    : t('CONVERSATION.OPEN_CONVERSATIONS_BANNER.SHOW')
);

const onAction = () => {
  const conversation = singleConversation.value;
  if (!conversation) {
    emit('showPreviousConversations');
    return;
  }

  // Stay in the inbox view only when the conversation belongs to that inbox.
  const inboxId =
    Number(activeInbox.value) === Number(conversation.inbox_id)
      ? activeInbox.value
      : undefined;
  router.push(
    frontendURL(
      conversationUrl({
        accountId: accountId.value,
        activeInbox: inboxId,
        id: conversation.id,
      })
    )
  );
};
</script>

<template>
  <Banner
    v-if="openConversations.length"
    color-scheme="warning"
    class="mx-2 mt-2 overflow-hidden rounded-lg"
    :banner-message="bannerMessage"
    :action-button-label="actionLabel"
    has-action-button
    @primary-action="onAction"
  />
</template>
